import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AnnouncementDto, BuildingDto } from "@klantpunt/shared";
import { Meta } from "../../app/Meta";
import { IconPlus } from "../../app/icons";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";

const key = (buildingId: string) => ["building-announcements", buildingId] as const;
const todayIso = () => new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Amsterdam" });
const fmt = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
const period = (a: AnnouncementDto) =>
  a.startsOn === a.endsOn ? fmt(a.startsOn) : `${fmt(a.startsOn)} t/m ${fmt(a.endsOn)}`;

/**
 * Tijdelijke banner (docs/SCHERMEN.md §3): een eigen tekst bovenaan alle schermen van het gebouw, van de eerste tot
 * en met de laatste dag. Daarna verdwijnt hij vanzelf.
 */
export function AnnouncementsPanel({ building: b }: { building: BuildingDto }) {
  const qc = useQueryClient();
  const toast = useToast();
  const list = useQuery({
    queryKey: key(b.id),
    queryFn: () => api<AnnouncementDto[]>(`/buildings/${b.id}/announcements`),
  });
  const [text, setText] = useState("");
  const [startsOn, setStartsOn] = useState(todayIso);
  const [endsOn, setEndsOn] = useState(todayIso);
  const add = useMutation({
    mutationFn: () =>
      api<AnnouncementDto[]>(`/buildings/${b.id}/announcements`, {
        method: "POST",
        body: { text: text.trim(), startsOn, endsOn },
      }),
    onSuccess: (r) => {
      qc.setQueryData(key(b.id), r);
      setText("");
      toast(
        startsOn <= todayIso() ? "De banner staat op de schermen" : `De banner komt op ${fmt(startsOn)} op de schermen`,
      );
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : "Opslaan mislukt"),
  });
  const remove = async (a: AnnouncementDto) => {
    try {
      qc.setQueryData(key(b.id), await api<AnnouncementDto[]>(`/announcements/${a.id}`, { method: "DELETE" }));
      toast("Banner weggehaald");
    } catch (e) {
      toast(e instanceof ApiError ? e.message : "Weghalen mislukt");
    }
  };
  const bad = endsOn < startsOn;
  const today = todayIso();

  return (
    <section className="panel" aria-labelledby="bs-banners">
      <h2 id="bs-banners">Tijdelijke banner</h2>
      <p className="muted small">
        Een eigen tekst bovenaan alle schermen van dit gebouw, bijvoorbeeld "Lift B vandaag tot 12:00 buiten gebruik".
        Na de laatste dag verdwijnt hij vanzelf.
      </p>
      <form
        className="banner-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim() && !bad) add.mutate();
        }}
      >
        <label className="field banner-text">
          <span className="label">Tekst</span>
          <input value={text} maxLength={140} onChange={(e) => setText(e.target.value)} />
        </label>
        <label className="field">
          <span className="label">Vanaf</span>
          <input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
        </label>
        <label className="field">
          <span className="label">Tot en met</span>
          <input
            type="date"
            value={endsOn}
            min={startsOn}
            aria-invalid={bad}
            onChange={(e) => setEndsOn(e.target.value)}
          />
        </label>
        <button type="submit" className="btn" disabled={add.isPending || !text.trim() || bad}>
          <IconPlus size={18} aria-hidden="true" />
          {add.isPending ? "Bezig met opslaan…" : "Banner"}
        </button>
      </form>
      {bad && <p className="hint err">De laatste dag ligt voor de eerste.</p>}
      {list.data && list.data.length > 0 && (
        <ul className="dir-items screens-list" aria-label="Banners">
          {list.data.map((a) => (
            <li key={a.id}>
              <span className="grow">
                <strong>{a.text}</strong>
                <span className="muted small block">
                  <Meta items={[period(a), a.createdBy]} />
                </span>
              </span>
              <span className={`chip ${a.startsOn <= today ? "tone tone-ok" : ""}`.trim()}>
                {a.startsOn <= today ? "Op het scherm" : "Gepland"}
              </span>
              <button type="button" className="btn small ghost danger" onClick={() => void remove(a)}>
                Weghalen
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
